// Pure helpers of the correction history: action labels, authorship, field diff and summaries.

type Data = Record<string, unknown>;

export type HistoryEvent = {
  action: string;
  origin?: string;
  author_name?: string;
  work_version?: number | null;
  parameters_version_at?: string | null;
  source_ids?: string[];
};

// Groups drive the filter of the revision history; every recorded action belongs to one group.
export const actionGroups: { id: string; label: string; actions: Record<string, string> }[] = [
  { id: "works", label: "Работы", actions: {
    add: "Работа добавлена", edit: "Исправление", patch: "Исправление",
    correction: "Корректировка утверждённой работы", merge: "Объединение работ", split: "Разделение работы",
    details: "Расшифровка и цены", discrepancy_decision: "Решение по расхождению",
    source_building: "Здание по документу", match: "Сопоставление редакций", unmatch: "Отмена сопоставления",
  } },
  { id: "norms", label: "Нормы", actions: {
    norm: "Норма назначена", replace_norm: "Норма заменена", remove_norm: "Норма снята",
    norm_basis_changed: "Изменилось основание комплексной нормы", catalog_changed: "Смена нормативной базы",
    catalog_confirmation: "Подтверждение нормативной базы",
    catalog_remark: "Замечание о смене базы снято",
  } },
  { id: "system", label: "Автоматические изменения", actions: {
    visual_reading_correction: "Исправлено чтение изображения",
    reanalysis: "Повторный анализ", norm_auto: "Автоматическая замена нормы",
    source_relationships: "Автоматическая расшифровка по источникам",
  } },
  { id: "project", label: "Проект, редакция и документы", actions: {
    project_update: "Проект изменён", revision_update: "Редакция изменена",
    document_building: "Здание документа", drawing_frame: "Границы листа", analysis_tier: "Режим обработки",
  } },
  { id: "estimate", label: "Расчёт и выгрузки", actions: {
    estimate_parameters: "Параметры расчёта", export: "Выгрузка", estimate_upload: "Загрузка сметы",
    estimate_import: "Разбор сметы", estimate_link: "Связь со сметой", estimate_unlink: "Связь со сметой снята",
  } },
  { id: "designer", label: "Сверка проекта", actions: {
    designer_upload: "Новая ВОР проектировщика", designer_prepare: "Колонки и область ВОР",
    designer_link: "Соответствие позиции ВОР", designer_check: "Протокол сверки",
    designer_decision: "Решение по позиции ВОР",
  } },
  { id: "svor", label: "СВОР", actions: {
    svor_set: "Раздел СВОР", svor_technical: "Техническая часть СВОР", svor_source: "Источник СВОР",
    svor_import: "Колонки источника СВОР", svor_round: "Раунд СВОР",
  } },
];

const labels: Record<string, string> = Object.assign({}, ...actionGroups.map(group => group.actions));

export const actionLabel = (action: string): string => labels[action] || "Действие";

// One-off maintenance scripts tag their reasons «kpp-roman-call-20260921: …»; the tag is for the logs.
const SCRIPT_TAG = /^[a-z0-9]+(?:-[a-z0-9]+)*-\d{8}(?::\s*|$)/;

/** An event reason as the estimator reads it: without the tag of the script that wrote it. */
export const reasonText = (reason: string | null | undefined): string => (reason ?? "").replace(SCRIPT_TAG, "").trim();

/** Comma-separated actions of a filter group; empty for "all". */
export const groupActions = (id: string): string | undefined => {
  const group = actionGroups.find(item => item.id === id);
  return group ? Object.keys(group.actions).join(",") : undefined;
};

/** A system action never reads as a person's decision; the launcher is named only when recorded. */
export const authorLabel = (event: HistoryEvent): string => {
  if (event.origin === "system") return event.author_name ? `Система · запуск: ${event.author_name}` : "Система";
  return event.author_name || "Автор не записан";
};

/** Calculation context of the event. Unknown parts are omitted, never guessed. */
export const contextLine = (event: HistoryEvent, format: (iso: string) => string): string => [
  event.work_version != null ? `Версия работы ${event.work_version}` : "",
  event.parameters_version_at ? `Параметры расчёта: версия от ${format(event.parameters_version_at)}` : "",
  event.source_ids?.length ? `Источников: ${event.source_ids.length}` : "",
].filter(Boolean).join(" · ");

const numeric = new Set(["quantity", "value", "known_quantity"]);

/** Order-independent form in which 1.50 and 1.5 are the same number. */
export const canonical = (key: string, value: unknown): unknown => {
  if (value == null) return null;
  if (Array.isArray(value)) return value.map(item => canonical(key, item));
  if (typeof value === "object") return Object.fromEntries(Object.entries(value as Data)
    .sort(([a], [b]) => a.localeCompare(b)).map(([k, v]) => [k, canonical(k, v)]));
  if (numeric.has(key) && typeof value === "string" && /^-?\d+(\.\d+)?$/.test(value))
    return value.includes(".") ? value.replace(/0+$/, "").replace(/\.$/, "") : value;
  return value;
};

/**
 * Keys whose value really changed. Older merge/split events stored only the successors in
 * `after`; a key missing there is "not recorded", not "cleared".
 */
export const changedKeys = (keys: string[], before: Data, after: Data, action = ""): string[] => {
  const partial = action === "merge" || action === "split";
  return keys.filter(key => !(partial && !(key in after))
    && JSON.stringify(canonical(key, before[key])) !== JSON.stringify(canonical(key, after[key])));
};

/** Works that replaced a merged or split work. Old events know only how many. */
export const successorsText = (after: Data, amount: (value: string) => string): string => {
  const rows = Array.isArray(after.successors) ? after.successors as Data[] : [];
  if (rows.length) return rows.map(row => [row.description,
    row.quantity == null ? "объём не установлен" : `${amount(String(row.quantity))} ${row.unit ?? ""}`.trim()]
    .filter(Boolean).join(" · ")).join("; ");
  const ids = Array.isArray(after.superseded_by) ? after.superseded_by : [];
  return ids.length ? `Работ: ${ids.length}` : "";
};

/** One meaningful line for events that change no work field. */
export const eventSummary = (action: string, before: Data, after: Data): string => {
  if (action === "export") {
    const format = ({
      package: "комплект ВОР", xlsx: "ВОР — Excel", docx: "ВОР — Word", grand: "ГРАНД-Смета",
      kac: "материалы и запрос КАЦ", protocol: "протокол расхождений", information: "информационная справка",
      full_estimate: "снимок сметы", direct_costs: "снимок прямых затрат",
    } as Record<string, string>)[String(after.format)] || String(after.format ?? "");
    return [`Формат: ${format || "не записан"}`, after.draft === true ? "черновик" : after.draft === false ? "итоговая" : ""].filter(Boolean).join(" · ");
  }
  if (action === "match") return after.replacement ? "Работы редакций сопоставлены как замена" : "Работы редакций сопоставлены как одна работа";
  if (action === "unmatch") return "Сопоставление работ редакций отменено";
  if (action === "svor_round") return ["resolved", "remaining", "new"].every(key => typeof after[key] === "number")
    ? `Снято замечаний: ${after.resolved} · осталось: ${after.remaining} · новых: ${after.new}` : "";
  if (action === "estimate_upload" && after.filename) return `Файл: ${after.filename}`;
  if (action === "merge" || action === "split") return "";
  return Object.keys(before).length || Object.keys(after).length ? "Поля работы не менялись; подробности — в данных версии." : "Значения работы не менялись.";
};
